import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
function AnimatedNumber({
  value,
  suffix,
  duration = 2
}: {
  value: number;
  suffix: string;
  duration?: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, v => Math.round(v));
  useEffect(() => {
    if (isInView) {
      const controls = animate(motionValue, value, {
        duration,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, value, duration, motionValue]);
  useEffect(() => {
    const unsubscribe = rounded.on('change', v => setDisplayValue(v));
    return unsubscribe;
  }, [rounded]);
  return <span ref={ref} data-id="element-729">
      {displayValue.toLocaleString()}
      {suffix}
    </span>;
}
export function Stats() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full bg-carbon-deep py-20 sm:py-24" data-id="element-730">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-731">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-732">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" data-id="element-733">
            Driving measurable impact
          </h2>
          <p className="mt-3 text-lg text-white/60" data-id="element-734">
            Trusted by organizations worldwide to deliver accurate carbon
            intelligence.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-735">
          <motion.div className="text-center" variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1]
            }
          }
        }} data-id="element-736">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-737">
              <AnimatedNumber value={500} suffix="M+" data-id="element-738" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-739">
              Tons Tracked
            </p>
          </motion.div>

          <motion.div className="text-center" variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1]
            }
          }
        }} data-id="element-740">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-741">
              <AnimatedNumber value={2000} suffix="+" data-id="element-742" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-743">
              Organizations
            </p>
          </motion.div>

          <motion.div className="text-center" variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1]
            }
          }
        }} data-id="element-744">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-745">
              <AnimatedNumber value={99} suffix=".9%" duration={1.5} data-id="element-746" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-747">
              Accuracy
            </p>
          </motion.div>

          <motion.div className="text-center" variants={{
          hidden: {
            opacity: 0,
            y: 30
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1]
            }
          }
        }} data-id="element-748">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-749">
              <AnimatedNumber value={150} suffix="+" data-id="element-750" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-751">
              Countries
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}